// Authentication flows that produce a ready Engine. The password is stretched locally;
// the server sees only a signature over a challenge (login) or public material
// (register). A forgotten password is unrecoverable by design - see SECURITY.md.
import { initCrypto, createAccount, deriveFromPassword, unwrapKeyBundle, sign } from "../crypto/index.js";
import { fromB64, toB64 } from "@shared/encoding.js";
import { Api } from "../net/api.js";
import { LocalStore, readAccount, writeAccount, wipeStore, type AccountRecord } from "../store/idb.js";
import { Engine } from "./engine.js";

export type OnChange = (convId: string) => void;

export async function bootCrypto(): Promise<void> {
  await initCrypto();
}

export async function register(handle: string, password: string, onChange: OnChange): Promise<Engine> {
  const acct = createAccount(handle, password);
  const api = new Api();
  const { user_id, token } = await api.register(acct.request);
  api.setToken(token);
  await writeAccount({ user_id, handle, token, salt: acct.request.salt, key_bundle: acct.request.key_bundle });
  const store = await LocalStore.open(acct.secrets.storeKey);
  return new Engine({ user_id, handle }, { identity: acct.secrets.identity, spk: acct.secrets.spk, opks: acct.secrets.opks }, api, store, onChange);
}

export async function login(handle: string, password: string, onChange: OnChange): Promise<Engine> {
  const api = new Api();
  const start = await api.loginStart(handle);
  const pw = deriveFromPassword(password, fromB64(start.salt));
  const signature = toB64(sign(fromB64(start.challenge), pw.authKeypair.priv));
  const fin = await api.loginFinish(start.challenge_id, signature);
  api.setToken(fin.token);
  const bundle = unwrapKeyBundle(pw.wrapKey, fin.key_bundle);
  await writeAccount({ user_id: fin.user_id, handle, token: fin.token, salt: start.salt, key_bundle: fin.key_bundle });
  const store = await LocalStore.open(pw.storeKey);
  return new Engine({ user_id: fin.user_id, handle }, bundle, api, store, onChange);
}

/** Offline-capable unlock from the cached account record. Throws on the wrong password. */
export async function unlock(password: string, onChange: OnChange): Promise<Engine> {
  const acct = await readAccount();
  if (!acct) throw new Error("no saved account on this device");
  const pw = deriveFromPassword(password, fromB64(acct.salt));
  const bundle = unwrapKeyBundle(pw.wrapKey, acct.key_bundle); // throws if the password is wrong
  const api = new Api(acct.token);
  const store = await LocalStore.open(pw.storeKey);
  return new Engine({ user_id: acct.user_id, handle: acct.handle }, bundle, api, store, onChange);
}

export const savedAccount = (): Promise<AccountRecord | null> => readAccount();

export async function logout(): Promise<void> {
  await wipeStore();
}

/** Recovery file: the same encrypted material the server holds. Useful with the
 * password to restore on a fresh device; it does NOT rescue a forgotten password. */
export async function exportRecovery(): Promise<string> {
  const acct = await readAccount();
  if (!acct) throw new Error("no account to export");
  return JSON.stringify({ v: 1, user_id: acct.user_id, handle: acct.handle, salt: acct.salt, key_bundle: acct.key_bundle }, null, 2);
}

export async function importRecovery(json: string): Promise<void> {
  const data = JSON.parse(json) as { user_id: string; handle: string; salt: string; key_bundle: string };
  if (!data.user_id || !data.handle || !data.salt || !data.key_bundle) throw new Error("not a valid recovery file");
  // Token is absent in a recovery file; the user must sign in once to obtain a session.
  await writeAccount({ ...data, token: "" });
}
